import { HttpParams } from "@angular/common/http";
import { Injectable } from '@angular/core';
import { Address } from "@models/address/address";
import { Individual } from "@models/individuals/individual";
import { IndividualDto } from "@models/individuals/individual-dto";
import { IndividualRecord } from "@models/individuals/Individual-record";
import { IndividualRecordShort } from "@models/individuals/individual-record-short";
import { IndividualVerificationModel } from "@models/individuals/individual-verification.model";
import { PageResult } from "@models/requests/page-result.interface";
import {
  SendIndividualToVerificationModel
} from "@pages/individuals/components/individual-profile-page/components/individual-id-verification/models/send-to-verification.model";
import BaseApiService from "@shared/services/base/base-api.service";
import { IServerSideGetRowsRequest } from "ag-grid-community";
import { map, Observable } from "rxjs";

@Injectable({ providedIn: 'root' })
export class IndividualsApiService extends BaseApiService {

  list(): Observable<IndividualRecord[]> {
    return this.api.get<IndividualRecord[]>(`${ this.apiUrl }/individuals`);
  }

  getSplitList(individualIdSplitFrom: string): Observable<IndividualRecordShort[]> {
    return this.api.get<IndividualRecordShort[]>(`${ this.apiUrl }/individuals/list/split/${ individualIdSplitFrom }`)
      .pipe(map(data => {
        data = data.map(d => new IndividualRecordShort(d));
        return data;
      }));
  }

  get(individualId: string): Observable<IndividualDto> {
    return this.api.get<IndividualDto>(`${ this.apiUrl }/individuals/${ individualId }`)
      .pipe(map((data) => new IndividualDto(data)));
  }

  merge(individualIdMergeTo: string, individualsId: string[]): Observable<void> {
    return this.api.post<void>(`${ this.apiUrl }/individuals/merge/${ individualIdMergeTo }`, individualsId);
  }

  updateAddress(individualId: string, address: Address): Observable<void> {
    return this.api.put<void>(`${ this.apiUrl }/individuals/${ individualId }/address`, address);
  }

  updateIndividualPart(part: 'din' | 'dob' | 'reference-number' | 'family-group', individualId: string, valueToUpdate: string | null): Observable<void> {
    return this.api.put<void>(
      `${ this.apiUrl }/individuals/${ individualId }/${ part }`,
      JSON.stringify(valueToUpdate),
      { headers: { 'Content-Type': 'application/json' } }
    );
  }

  getGridList(request: IServerSideGetRowsRequest, search?: string): Observable<PageResult<IndividualRecord>> {
    let params = new HttpParams();
    if (search) {
      params = params.append('search', search);
    }

    return this.api.post<PageResult<IndividualRecord>>(`${ this.apiUrl }/individuals/grid`, request, { params: params })
      .pipe(map(data => {
        data.records = data.records.map(d => new IndividualRecord(d));
        return data;
      }));
  }

  split(individualIdSplitFrom: string, relationshipsIdToMove: string[], individualIdSplitTo?: string): Observable<void> {
    let params = new HttpParams();
    if (individualIdSplitTo !== undefined) {
      params = params.append('individualIdSplitTo', individualIdSplitTo);
    }
    return this.api.post<void>(`${ this.apiUrl }/individuals/split/${ individualIdSplitFrom }`, relationshipsIdToMove, { params: params });
  }

  checkIndividualByDin(din: string, fullName: string): Observable<boolean> {
    return this.api.post<boolean>(`${ this.apiUrl }/individuals/check/din`, { din, fullName });
  }

  triggerVerification(individualId: string, sendModel: SendIndividualToVerificationModel): Observable<IndividualVerificationModel> {
    return this.api.post<IndividualVerificationModel>(`${ this.apiUrl }/individuals/${ individualId }/verification/send`, sendModel);
  }

  getVerification(individualId: string): Observable<IndividualVerificationModel> {
    return this.api.get<IndividualVerificationModel>(`${ this.apiUrl }/individuals/${ individualId }/verification`);
  }

  cancelVerification(individualId: string): Observable<IndividualVerificationModel> {
    return this.api.post<IndividualVerificationModel>(`${ this.apiUrl }/individuals/${ individualId }/verification/cancel`, null);
  }

  resetVerification(individualId: string): Observable<IndividualVerificationModel> {
    return this.api.post<IndividualVerificationModel>(`${ this.apiUrl }/individuals/${ individualId }/verification/reset`, null);
  }

  downloadSertificate(individualId: string): Observable<string> {
    return this.api.get<string>(`${ this.apiUrl }/individuals/${ individualId }/verification/certificate`);
  }

  getIndividualListWithContacts(individualsId: string[]): Observable<Individual[]> {
    return this.api.post<Individual[]>(`${ this.apiUrl }/individuals/contacts`, individualsId);
  }
}
